import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Box,
  TextField as MuiTextField
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../../src/components';
import { dropdownFetch, isToday, request, toast } from '../../../../../../src/utils';

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const formatRegTime = (time) => {
  const [hours, minutes] = time.split(':');
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  // Format to 12-hour time
  const options = { hour: '2-digit', minute: '2-digit', hour12: true };
  return date.toLocaleTimeString('en-US', options);
};

const DETAIL_HEADER = [
  {
    header: 'Schedule Date & Time',
    accessor: (row) => `${formatDate(row.date)}/${formatRegTime(row?.time)}`
  },
  {
    header: 'Schedule No.',
    accessor: (row) => row?.schedule_no
  },
  {
    header: 'Patient Name',
    accessor: (row) => `${row.patient.name}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Adm No.',
    accessor: 'ipd_no'
  },
  {
    header: 'OT',
    accessor: (row) => row?.otDetails[0]?.operationtheatre
  },
  {
    header: 'Operation Type',
    accessor: (row) => row?.surgery_items[0]?.surgery_type
  },
  {
    header: 'Doctor Name',
    accessor: (row) => row?.resource_detail.map((data) => data?.doctorName).join(', ')
  }
];

const initialIndoorStatusDataObj = {
  data: [],
  loading: false
};

export const OtScheduleStatusList = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  const methods = useForm({
    defaultValues: {},
    mode: 'all'
  });
  const { handleSubmit, setValue, reset, watch } = methods;

  const searchIPDDetails = async () => {
    setLoading(true);
    const response = await request('/ot-schedule-list', {
      method: 'GET'
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setDetailList({ loading: false, data: response?.data });
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    searchIPDDetails();
  }, []);

  const handleSearchByDate = async () => {
    setDetailList({ loading: true, data: [] });
    try {
      const query = {};
      if (watch('from_date') && watch('to_date')) {
        query.startDate = watch('from_date');
        query.endDate = watch('to_date');
      }
      const response = await request('/ot-schedule-list', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        setDetailList({ loading: false, data: response?.data });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
        setDetailList([]);
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setDetailList([]);
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                OT Schedule Calender
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}></Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>From Date </Typography>
                    <TextField
                      // disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 1 }}
                      name="from_date"
                      type="date"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField
                      // disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 1 }}
                      name="to_date"
                      type="date"
                    />
                  </Stack>
                </Grid>
                <Button
                  variant="contained"
                  disabled={!watch('from_date') || !watch('to_date')}
                  onClick={handleSearchByDate}
                  sx={{ marginLeft: '1%', height: '100%', marginTop: '10px' }} 
                >
                  Search
                </Button>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Table
                    containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                    data={detailList?.data || []}
                    columns={DETAIL_HEADER}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
